import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Divider,
  Fab,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import { GLYPHS } from '@/components/ui';
import { CalendarTabProps, WorkSchedule } from '../../types/employee';
import { displayEmployeeName, scheduleChipLabel } from '../../utils/workScheduleDisplay';
import { buildEmployeeColorMap, chipTextColorFor } from '../../utils/employeeColors';
import MonthYearSelector from '../MonthYearSelector';
import SectionHeader from '../SectionHeader';
import { useEmployeeData, useWorkScheduleData, useEmployeeActions } from '../../hooks';
import WorkScheduleForm from './WorkScheduleForm';
import WeekTimeGrid, { GridSegment, GridCommitMode } from './WeekTimeGrid';
import CopyModeBanner from './CopyModeBanner';
import { Timestamp } from 'firebase/firestore';
import { formatLocalDateString } from '../../utils/dateUtils';
import { useWorkTimeTemplateStore } from '../../store/workTimeTemplateStore';
import { getHoursBetween } from '../../utils/payrollCalculations';
import { useAccountBookStore } from '../../store/accountBookStore';


const CalendarTab: React.FC<CalendarTabProps> = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'timeline'>('month');
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<WorkSchedule | null>(null);
  const [clickedDate, setClickedDate] = useState<Date | null>(null);
  // 드래그 이동 피드백
  const [dragOverDate, setDragOverDate] = useState<string | null>(null);
  const [moveSnackbar, setMoveSnackbar] = useState<string | null>(null);

  // 드롭 이동 직후 "시간도 수정할까요?" 후속 팝업
  interface MoveFollowUp {
    scheduleId: string;
    name: string;
    targetDate: Date;
    /** 이동 시점에 계산된 새 시간들 (리스너 반영 전에도 정확한 프리필용) */
    wt1?: { start: Date; end: Date };
    wt2?: { start: Date; end: Date };
    start?: Date;
    end?: Date;
  }
  const [moveFollowUp, setMoveFollowUp] = useState<MoveFollowUp | null>(null);
  const [editingTime, setEditingTime] = useState(false);
  const [timeForm, setTimeForm] = useState({ start: '09:00', end: '18:00' });
  const [savingTime, setSavingTime] = useState(false);

  // 일정 복사 모드
  const [copyMode, setCopyMode] = useState(false);
  const [copySourceDate, setCopySourceDate] = useState<string | null>(null); // YYYY-MM-DD
  const [copyTargetDates, setCopyTargetDates] = useState<string[]>([]);
  const [copying, setCopying] = useState(false);
  const [copyResultSnackbar, setCopyResultSnackbar] = useState<string | null>(null);

  // 실제 데이터 사용
  const { employees, loading: employeesLoading, error: employeesError } = useEmployeeData();
  const userId = useAccountBookStore((state) => state.userId);
  const { templates, fetchTemplates, getTemplateById } = useWorkTimeTemplateStore();
  
  // 월 전체 데이터를 불러오기 위해 selectedDate의 월 첫째 날로 설정.
  // 같은 월이면 동일 참조를 유지해 useWorkScheduleData 가 재구독되지 않게 한다.
  const monthStartDate = useMemo(
    () => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1),
    [selectedDate]
  );
  // 주간/시간표 모드에서 주(일~토)가 두 달에 걸치면 인접 월 스케줄도 함께 구독
  const weekBoundaryDate = useMemo(() => {
    if (viewMode === 'month') return undefined;
    const start = new Date(selectedDate);
    start.setDate(selectedDate.getDate() - selectedDate.getDay());
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    if (start.getMonth() === end.getMonth()) return undefined;
    return start.getMonth() === selectedDate.getMonth() ? end : start;
  }, [viewMode, selectedDate]);
  const { schedules, loading: schedulesLoading, error: schedulesError } = useWorkScheduleData(monthStartDate, weekBoundaryDate);
  const { deleteSchedule, updateSchedule, copyDaySchedules } = useEmployeeActions();

  // 직원별 캘린더 색 — 등록순으로 chartPalette 12색 자동 배정
  const employeeColors = useMemo(
    () => buildEmployeeColorMap(employees),
    [employees]
  );
  
  // 템플릿 데이터 가져오기
  useEffect(() => {
    if (userId) {
      fetchTemplates(userId);
    }
  }, [userId, fetchTemplates]);
  
  const loading = employeesLoading || schedulesLoading;
  const error = employeesError || schedulesError;

  // 날짜 변경 함수
  const handleDateChange = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    } else {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    }
    setSelectedDate(newDate);
  };

  // 일정 관리 함수들
  const handleScheduleDialogOpen = (date?: Date, schedule?: WorkSchedule) => {
    setClickedDate(date || null);
    setSelectedSchedule(schedule || null);
    setScheduleDialogOpen(true);
  };

  const handleScheduleDialogClose = () => {
    setScheduleDialogOpen(false);
    setSelectedSchedule(null);
    setClickedDate(null);
  };

  const handleScheduleDelete = async (schedule: WorkSchedule) => {
    if (window.confirm('이 근무 일정을 삭제하시겠습니까?')) {
      try {
        await deleteSchedule( schedule.id);
      } catch (error) {
        console.error('일정 삭제 실패:', error);
        alert('일정 삭제에 실패했습니다.');
      }
    }
  };

  // 오늘로 이동
  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // 선택된 날짜의 스케줄 가져오기
  const getSchedulesForDate = (date: Date) => {
    const dateStr = formatLocalDateString(date); // YYYY-MM-DD 형식
    return schedules.filter(schedule => {
      // schedule.date가 ISO 형식이거나 YYYY-MM-DD 형식일 수 있으므로 정규화
      const scheduleDateStr = typeof schedule.date === 'string'
        ? schedule.date.split('T')[0] // ISO 형식이면 날짜 부분만 추출
        : schedule.date;
      return scheduleDateStr === dateStr || scheduleDateStr.startsWith(dateStr);
    });
  };

  // ── 일정 복사 모드 ─────────────────────────────────
  const exitCopyMode = () => {
    setCopyMode(false);
    setCopySourceDate(null);
    setCopyTargetDates([]);
  };

  const sourceSchedules = copySourceDate ? getSchedulesForDate(new Date(copySourceDate)) : [];
  const sourceEmployeeNames = Array.from(
    new Set(sourceSchedules.map((s) => displayEmployeeName(s, employees))),
  );
  const sourceLabel =
    sourceEmployeeNames.length === 0
      ? ''
      : sourceEmployeeNames.length === 1
      ? sourceEmployeeNames[0]
      : `${sourceEmployeeNames[0]} 외 ${sourceEmployeeNames.length - 1}명`;

  // 복사 모드에서 날짜 셀 클릭 처리
  const handleCopyCellClick = (date: Date) => {
    const ds = formatLocalDateString(date);
    if (!copySourceDate) {
      const has = getSchedulesForDate(date).length > 0;
      if (!has) {
        setCopyResultSnackbar('이 날짜에는 복사할 일정이 없습니다.');
        return;
      }
      setCopySourceDate(ds);
      return;
    }
    if (ds === copySourceDate) return; // 원본은 대상이 될 수 없음
    setCopyTargetDates((prev) =>
      prev.includes(ds) ? prev.filter((d) => d !== ds) : [...prev, ds],
    );
  };

  const handleCopyExecute = async () => {
    if (!copySourceDate || copyTargetDates.length === 0) return;
    setCopying(true);
    try {
      const { copied, skipped } = await copyDaySchedules(copySourceDate, copyTargetDates);
      setCopyResultSnackbar(
        skipped > 0
          ? `${copied}건 복사 완료, ${skipped}건은 기존 일정이 있어 건너뜀`
          : `${copied}건 복사 완료`,
      );
      exitCopyMode();
    } catch (e) {
      console.error('일정 복사 실패:', e);
      setCopyResultSnackbar('일정 복사에 실패했습니다.');
    } finally {
      setCopying(false);
    }
  };

  /* ── 드래그 이동 (구글 캘린더식) ───────────────────────── */

  const parseTimeValue = (timeValue: unknown): Date | null => {
    if (!timeValue) return null;
    if (timeValue instanceof Date) return isNaN(timeValue.getTime()) ? null : timeValue;
    if (typeof timeValue === 'string') {
      const parsed = new Date(timeValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (typeof timeValue === 'object' && 'toDate' in (timeValue as object)) {
      return (timeValue as { toDate: () => Date }).toDate();
    }
    return null;
  };

  // Flutter 저장 형식과 동일한 로컬 ISO (타임존 'Z' 없음)
  const localIso = (d: Date) =>
    `${formatLocalDateString(d)}T${String(d.getHours()).padStart(2, '0')}:${String(
      d.getMinutes(),
    ).padStart(2, '0')}:00`;

  /** 월간/주간 뷰: 칩을 다른 날짜 셀로 드롭 → 시각 유지한 채 날짜만 이동 */
  const moveScheduleToDate = async (scheduleId: string, targetDate: Date) => {
    const schedule = schedules.find((s) => s.id === scheduleId);
    if (!schedule) return;
    const targetStr = formatLocalDateString(targetDate);
    const currentStr = (schedule.date || '').split('T')[0];
    if (targetStr === currentStr) return;

    // 기존 시각을 새 날짜에 부착
    const rebase = (raw: unknown): Date | null => {
      const t = parseTimeValue(raw);
      if (!t) return null;
      const d = new Date(targetDate);
      d.setHours(t.getHours(), t.getMinutes(), 0, 0);
      return d;
    };
    const rebaseRange = (start: unknown, end: unknown) => {
      const s = rebase(start);
      const e = rebase(end);
      if (!s || !e) return null;
      if (e.getTime() <= s.getTime()) e.setDate(e.getDate() + 1); // 야간 근무
      return { start: s, end: e };
    };

    const updates: Partial<WorkSchedule> = { date: targetStr };
    if (schedule.workTime1 || schedule.workTime2) {
      if (schedule.workTime1) {
        const r = rebaseRange(schedule.workTime1.start, schedule.workTime1.end);
        if (r) updates.workTime1 = r;
      }
      if (schedule.workTime2) {
        const r = rebaseRange(schedule.workTime2.start, schedule.workTime2.end);
        if (r) updates.workTime2 = r;
      }
    } else if (schedule.start_time && schedule.end_time) {
      const r = rebaseRange(schedule.start_time, schedule.end_time);
      if (r) {
        updates.start_time = localIso(r.start);
        updates.end_time = localIso(r.end);
      }
    }

    try {
      await updateSchedule(schedule.id, updates);
      const name = displayEmployeeName(schedule, employees);
      // 이동 완료 → "시간도 수정할까요?" 후속 팝업 (이동 시점에 계산한 새 시간을 보관)
      setEditingTime(false);
      setMoveFollowUp({
        scheduleId: schedule.id,
        name,
        targetDate,
        wt1: updates.workTime1,
        wt2: updates.workTime2,
        start: updates.start_time ? new Date(updates.start_time) : undefined,
        end: updates.end_time ? new Date(updates.end_time) : undefined,
      });
    } catch (err) {
      console.error('일정 이동 실패:', err);
      alert('일정 이동에 실패했습니다.');
    }
  };

  /** 후속 팝업 — 현재(이동 후) 출근·퇴근 시각으로 시간 폼 프리필 */
  const openQuickTimeEdit = () => {
    if (!moveFollowUp) return;
    const startDate = moveFollowUp.wt1?.start ?? moveFollowUp.start;
    const endDate = moveFollowUp.wt2?.end ?? moveFollowUp.wt1?.end ?? moveFollowUp.end;
    const toHHmm = (d?: Date) =>
      d && !isNaN(d.getTime())
        ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
        : '';
    setTimeForm({ start: toHHmm(startDate) || '09:00', end: toHHmm(endDate) || '18:00' });
    setEditingTime(true);
  };

  /** 간략 시간 수정 저장 — 출근=첫 구간 시작, 퇴근=마지막 구간 종료 */
  const handleQuickTimeSave = async () => {
    if (!moveFollowUp) return;
    const { scheduleId, targetDate, name, wt1, wt2 } = moveFollowUp;
    const atTarget = (hhmm: string) => {
      const [h, m] = hhmm.split(':').map(Number);
      const d = new Date(targetDate);
      d.setHours(h || 0, m || 0, 0, 0);
      return d;
    };
    const newStart = atTarget(timeForm.start);
    const newEnd = atTarget(timeForm.end);
    if (newEnd.getTime() <= newStart.getTime()) newEnd.setDate(newEnd.getDate() + 1); // 야간

    const updates: Partial<WorkSchedule> = { date: formatLocalDateString(targetDate) };
    if (wt1) {
      if (wt2) {
        // 2구간: 출근 = 1차 시작, 퇴근 = 2차 종료 (중간 경계 유지)
        updates.workTime1 = { start: newStart, end: wt1.end };
        updates.workTime2 = { start: wt2.start, end: newEnd };
      } else {
        updates.workTime1 = { start: newStart, end: newEnd };
      }
    } else {
      updates.start_time = localIso(newStart);
      updates.end_time = localIso(newEnd);
    }

    setSavingTime(true);
    try {
      await updateSchedule(scheduleId, updates);
      setMoveSnackbar(`${name} 일정 시간을 ${timeForm.start} - ${timeForm.end}로 수정했습니다`);
      setMoveFollowUp(null);
      setEditingTime(false);
    } catch (err) {
      console.error('시간 수정 실패:', err);
      alert('시간 수정에 실패했습니다.');
    } finally {
      setSavingTime(false);
    }
  };

  /** 팝업 닫기 (아니요/취소) — 이동 자체는 이미 완료된 상태 */
  const closeFollowUp = () => {
    if (savingTime) return;
    if (moveFollowUp) {
      setMoveSnackbar(
        `${moveFollowUp.name} 일정을 ${moveFollowUp.targetDate.getMonth() + 1}/${moveFollowUp.targetDate.getDate()}로 옮겼습니다`,
      );
    }
    setMoveFollowUp(null);
    setEditingTime(false);
  };

  /** 시간표 뷰: 블록 드래그/리사이즈 커밋 */
  const handleGridCommit = async (
    segment: GridSegment,
    newStart: Date,
    newEnd: Date,
    mode: GridCommitMode,
  ) => {
    const schedule = segment.schedule;
    const minutesOfDay = (d: Date) => d.getHours() * 60 + d.getMinutes();
    const updates: Partial<WorkSchedule> = {};

    if (schedule.workTime1 || schedule.workTime2) {
      // 현재 저장된 구간들을 파싱
      const parseRange = (range: { start: unknown; end: unknown }) => {
        const s = parseTimeValue(range.start);
        const e = parseTimeValue(range.end);
        return s && e ? { start: s, end: e } : null;
      };
      const wt1 = schedule.workTime1 ? parseRange(schedule.workTime1) : null;
      const wt2 = schedule.workTime2 ? parseRange(schedule.workTime2) : null;

      if (mode === 'move') {
        // 두 구간 모두 같은 날짜·분 오프셋으로 이동 (스케줄은 한 덩어리)
        updates.date = formatLocalDateString(newStart);
        const minuteDelta = minutesOfDay(newStart) - minutesOfDay(segment.start);
        const shiftRange = (r: { start: Date; end: Date }) => {
          const startMin = minutesOfDay(r.start) + minuteDelta;
          let endMin = minutesOfDay(r.end) + minuteDelta;
          if (minutesOfDay(r.end) <= minutesOfDay(r.start)) endMin += 24 * 60; // 야간
          const y = newStart.getFullYear();
          const m = newStart.getMonth();
          const day = newStart.getDate();
          return {
            start: new Date(y, m, day, 0, startMin),
            end: new Date(y, m, day, 0, endMin),
          };
        };
        if (wt1) updates.workTime1 = shiftRange(wt1);
        if (wt2) updates.workTime2 = shiftRange(wt2);
      } else {
        // 리사이즈: 드래그한 구간의 종료 시각만 변경
        if (segment.seg === 1 && wt1) wt1.end = newEnd;
        if (segment.seg === 2 && wt2) wt2.end = newEnd;
        if (wt1) updates.workTime1 = wt1;
        if (wt2) updates.workTime2 = wt2;
      }
    } else if (schedule.start_time && schedule.end_time) {
      if (mode === 'move') {
        updates.date = formatLocalDateString(newStart);
        updates.start_time = localIso(newStart);
        updates.end_time = localIso(newEnd);
      } else {
        const s = parseTimeValue(schedule.start_time);
        if (!s) return;
        updates.start_time = localIso(s);
        updates.end_time = localIso(newEnd);
      }
    } else {
      return;
    }

    try {
      await updateSchedule(schedule.id, updates);
      const name = displayEmployeeName(schedule, employees);
      setMoveSnackbar(
        mode === 'move'
          ? `${name} 일정을 ${newStart.getMonth() + 1}/${newStart.getDate()} ${String(newStart.getHours()).padStart(2, '0')}:${String(newStart.getMinutes()).padStart(2, '0')}로 옮겼습니다`
          : `${name} 퇴근 시간을 ${String(newEnd.getHours()).padStart(2, '0')}:${String(newEnd.getMinutes()).padStart(2, '0')}로 변경했습니다`,
      );
    } catch (err) {
      console.error('일정 변경 실패:', err);
      alert('일정 변경에 실패했습니다.');
    }
  };

  /** 날짜 셀 공용 드롭 핸들러 (월간/주간) */
  const dropHandlers = (date: Date) => ({
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    },
    onDragEnter: () => setDragOverDate(formatLocalDateString(date)),
    onDragLeave: () => setDragOverDate(null),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDragOverDate(null);
      const id = e.dataTransfer.getData('text/schedule-id');
      if (id) moveScheduleToDate(id, date);
    },
  });

  // 직원 색 칩 — 월간/주간 뷰 공용
  const renderScheduleChip = (schedule: WorkSchedule, idx: number) => {
    const chipLabel = scheduleChipLabel(schedule, employees);
    // 미할당/퇴직 스냅샷 스케줄은 중립 스타일로 구분
    const chipColor = schedule.employee_id
      ? employeeColors[schedule.employee_id]
      : undefined;

    return (
      <Chip
        key={schedule.id || idx}
        label={chipLabel}
        size="small"
        draggable={!copyMode}
        onDragStart={(e: React.DragEvent) => {
          e.stopPropagation();
          e.dataTransfer.setData('text/schedule-id', schedule.id);
          e.dataTransfer.effectAllowed = 'move';
        }}
        sx={{
          fontSize: '0.7rem',
          height: 'auto',
          minHeight: 16,
          mb: 0.5,
          maxWidth: '100%',
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          ...(chipColor
            ? {
                background: chipColor,
                color: chipTextColorFor(chipColor),
              }
            : {
                background: 'var(--bg-elev-2)',
                color: 'var(--fg-strong)',
                border: '1px dashed var(--border-subtle)',
              }),
          '& .MuiChip-label': {
            px: 0.5,
            py: 0.25,
            whiteSpace: 'normal',
            wordBreak: 'break-all',
            lineHeight: 1.2,
          },
        }}
      />
    );
  };

  // 캘린더 헤더 렌더링
  const renderCalendarHeader = () => (
    <Box sx={{ 
      mb: 3,
      p: 2,
      background: 'var(--bg-elev-1)',
      borderRadius: 2,
      border: '1px solid var(--border-subtle)',
    }}>
      <SectionHeader
        eyebrow="근무 일정"
        title="근무 일정 관리"
        subtitle="직원별 근무 일정을 확인하고 관리하세요"
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={(_, v) => v != null && setViewMode(v)}
            >
              <ToggleButton value="month">월간</ToggleButton>
              <ToggleButton value="week">주간</ToggleButton>
              <ToggleButton value="timeline">시간표</ToggleButton>
            </ToggleButtonGroup>
            <Button
              size="small"
              variant="outlined"
              color={copyMode ? 'inherit' : 'primary'}
              onClick={() => {
                if (copyMode) {
                  exitCopyMode();
                } else {
                  setViewMode('month');
                  setCopyMode(true);
                }
              }}
            >
              {copyMode ? '복사 취소' : '일정 복사'}
            </Button>
            <IconButton size="small" onClick={() => handleDateChange('prev')} aria-label="이전"
              sx={{ fontSize: 22, lineHeight: 1, color: 'var(--fg-secondary)' }}>
              {GLYPHS.prev}
            </IconButton>
            <Button variant="text" size="small" onClick={goToToday}>오늘</Button>
            <IconButton size="small" onClick={() => handleDateChange('next')} aria-label="다음"
              sx={{ fontSize: 22, lineHeight: 1, color: 'var(--fg-secondary)' }}>
              {GLYPHS.next}
            </IconButton>
            <Box sx={{ transform: 'scale(0.7)', transformOrigin: 'right center' }}>
              <MonthYearSelector
                selectedDate={selectedDate}
                onDateChange={(newDate) => setSelectedDate(newDate)}
              />
            </Box>
          </Box>
        }
      />
    </Box>
  );

  // 월간 캘린더 렌더링
  const renderMonthCalendar = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    // 6주 * 7일 = 42일
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return (
      <Grid container spacing={1}>
        {/* 요일 헤더 */}
        {['일', '월', '화', '수', '목', '금', '토'].map((day, index) => (
          <Grid item xs={12/7} key={day}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                textAlign: 'center', 
                fontWeight: 600,
                color: index === 0 ? 'var(--c-red-600)' : index === 6 ? 'var(--c-cool-700)' : 'var(--fg-strong)',
                py: 1,
              }}
            >
              {day}
            </Typography>
          </Grid>
        ))}
        
        {/* 날짜 셀들 */}
        {days.map((date, index) => {
          const isCurrentMonth = date.getMonth() === month;
          const isToday = date.toDateString() === new Date().toDateString();
          const daySchedules = getSchedulesForDate(date);
          
          const isDragOver = dragOverDate === formatLocalDateString(date);
            const dsLocal = formatLocalDateString(date);
            const isCopySource = copyMode && copySourceDate === dsLocal;
            const isCopyTarget = copyMode && copyTargetDates.includes(dsLocal);
          return (
            <Grid item xs={12/7} key={index}>
              <Paper
                {...(copyMode ? {} : dropHandlers(date))}
                sx={{
                  minHeight: 80,
                  p: 1,
                  background: isCopyTarget
                    ? 'var(--c-orange-50)'
                    : isDragOver
                    ? 'var(--primary-soft)'
                    : isCurrentMonth
                    ? (isToday ? 'var(--primary-bg)' : 'var(--bg-base)')
                    : 'var(--bg-elev-2)',
                  border: isCopySource
                    ? '2px solid var(--c-cool-700)'
                    : isCopyTarget
                    ? '2px solid var(--c-orange-600)'
                    : isDragOver
                    ? '2px dashed var(--primary)'
                    : isToday
                    ? '2px solid var(--primary)'
                    : '1px solid var(--border-subtle)',
                  borderRadius: 1,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: isCopyTarget
                      ? 'var(--c-orange-50)'
                      : isCopySource
                      ? 'var(--bg-base)'
                      : isCurrentMonth
                      ? 'var(--bg-green)'
                      : 'var(--bg-elev-2)',
                    transform: 'scale(1.02)',
                  },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (copyMode) {
                    handleCopyCellClick(date);
                  } else {
                    setSelectedDate(date);
                  }
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: isToday ? 700 : 500,
                    color: isCurrentMonth 
                      ? (isToday ? 'var(--primary)' : 'var(--fg-strong)')
                      : 'var(--fg-disabled)',
                    mb: 0.5,
                  }}
                >
                  {date.getDate()}
                </Typography>
                
                {daySchedules.map((schedule, idx) => renderScheduleChip(schedule, idx))}
                
                {daySchedules.length > 2 && (
                  <Typography variant="caption" sx={{ color: 'var(--fg-secondary)' }}>
                    +{daySchedules.length - 2}명 더
                  </Typography>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // 주간 캘린더 렌더링 — 선택 날짜가 속한 주(일~토), 칩 전체 표시
  const renderWeekCalendar = () => {
    const weekStart = new Date(selectedDate);
    weekStart.setDate(selectedDate.getDate() - selectedDate.getDay());
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
    const weekEnd = days[6];
    const fmt = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'var(--fg-secondary)', fontWeight: 600 }}>
          {weekStart.getFullYear()}년 {fmt(weekStart)} ~ {fmt(weekEnd)}
        </Typography>
        <Grid container spacing={1}>
          {days.map((date, index) => {
            const isToday = date.toDateString() === new Date().toDateString();
            const isSelected = date.toDateString() === selectedDate.toDateString();
            const daySchedules = getSchedulesForDate(date);

            const isDragOver = dragOverDate === formatLocalDateString(date);
            return (
              <Grid item xs={12 / 7} key={index}>
                <Paper
                  {...dropHandlers(date)}
                  sx={{
                    minHeight: 200,
                    p: 1,
                    background: isDragOver
                      ? 'var(--primary-soft)'
                      : isToday ? 'var(--primary-bg)' : 'var(--bg-base)',
                    border: isDragOver
                      ? '2px dashed var(--primary)'
                      : isToday
                      ? '2px solid var(--primary)'
                      : isSelected
                        ? '2px solid var(--c-cool-700)'
                        : '1px solid var(--border-subtle)',
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'var(--bg-green)',
                      transform: 'scale(1.02)',
                    },
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedDate(date);
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isToday ? 700 : 600,
                      color:
                        index === 0
                          ? 'var(--c-red-600)'
                          : index === 6
                            ? 'var(--c-cool-700)'
                            : isToday
                              ? 'var(--primary)'
                              : 'var(--fg-strong)',
                      mb: 1,
                    }}
                  >
                    {dayNames[index]} {date.getDate()}일
                  </Typography>

                  {daySchedules.length === 0 ? (
                    <Typography variant="caption" sx={{ color: 'var(--fg-disabled)' }}>
                      일정 없음
                    </Typography>
                  ) : (
                    daySchedules.map((schedule, idx) => renderScheduleChip(schedule, idx))
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  // 선택된 날짜의 상세 정보
  const renderSelectedDateDetails = () => {
    const daySchedules = getSchedulesForDate(selectedDate);
    
    return (
      <Card sx={{ mt: 0 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--fg-strong)' }}>
              {selectedDate.toLocaleDateString('ko-KR', {
                month: 'long',
                day: 'numeric',
                weekday: 'short'
              })}
            </Typography>
          </Box>

          <Divider sx={{ mb: 1 }} />

          {daySchedules.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'var(--fg-tertiary)', textAlign: 'center', py: 2 }}>
              이 날에는 근무 일정이 없습니다.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: { md: 560 }, overflowY: 'auto' }}>
              {daySchedules.map((schedule) => {
                const employeeName = displayEmployeeName(schedule, employees);
                // 캘린더 칩과 동일한 직원 색을 좌측 보더로 연결
                const accentColor = schedule.employee_id
                  ? employeeColors[schedule.employee_id]
                  : undefined;
                const accentBorder = `4px solid ${accentColor ?? 'var(--border-subtle)'}`;
                
                // 안전한 날짜 파싱 함수
                const parseTime = (timeValue: any): Date | null => {
                  if (!timeValue) return null;
                  if (timeValue instanceof Date) return timeValue;
                  if (typeof timeValue === 'string') {
                    const parsed = new Date(timeValue);
                    return isNaN(parsed.getTime()) ? null : parsed;
                  }
                  if (timeValue && typeof timeValue === 'object' && 'toDate' in timeValue) {
                    return timeValue.toDate();
                  }
                  return null;
                };
                
                // 시간을 "X시간 Y분" 형식으로 포맷하는 헬퍼 함수
                const formatHoursAndMinutes = (hours: number): string => {
                  const totalMinutes = Math.round(hours * 60);
                  const h = Math.floor(totalMinutes / 60);
                  const m = totalMinutes % 60;
                  if (h === 0) {
                    return `${m}분`;
                  } else if (m === 0) {
                    return `${h}시간`;
                  } else {
                    return `${h}시간 ${m}분`;
                  }
                };
                
                // 템플릿 기반 스케줄인 경우 (workTime1/workTime2는 getWorkSchedulesByUserFromShared와 onWorkSchedulesSnapshot에서 파싱됨)
                if (schedule.templateId || schedule.workTime1 || schedule.workTime2) {
                  const template = schedule.templateId ? getTemplateById(schedule.templateId) : null;
                  const templateName = template?.name || '템플릿';
                  
                  // workTime1, workTime2 계산
                  let time1Display = '';
                  let time2Display = '';
                  let totalWorkHours = 0;
                  
                  if (schedule.workTime1) {
                    const start1 = schedule.workTime1.start instanceof Date 
                      ? schedule.workTime1.start 
                      : new Date(schedule.workTime1.start);
                    const end1 = schedule.workTime1.end instanceof Date 
                      ? schedule.workTime1.end 
                      : new Date(schedule.workTime1.end);
                    
                    if (!isNaN(start1.getTime()) && !isNaN(end1.getTime())) {
                      const hours1 = getHoursBetween(start1, end1);
                      totalWorkHours += hours1;
                      const hours1Str = formatHoursAndMinutes(hours1);
                      time1Display = `1: ${start1.toLocaleTimeString('ko-KR', { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: false
                      })} - ${end1.toLocaleTimeString('ko-KR', { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: false
                      })} (${hours1Str})`;
                    }
                  }
                  
                  if (schedule.workTime2) {
                    const start2 = schedule.workTime2.start instanceof Date 
                      ? schedule.workTime2.start 
                      : new Date(schedule.workTime2.start);
                    const end2 = schedule.workTime2.end instanceof Date 
                      ? schedule.workTime2.end 
                      : new Date(schedule.workTime2.end);
                    
                    if (!isNaN(start2.getTime()) && !isNaN(end2.getTime())) {
                      const hours2 = getHoursBetween(start2, end2);
                      totalWorkHours += hours2;
                      const hours2Str = formatHoursAndMinutes(hours2);
                      time2Display = `2: ${start2.toLocaleTimeString('ko-KR', { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: false
                      })} - ${end2.toLocaleTimeString('ko-KR', { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: false
                      })} (${hours2Str})`;
                    }
                  }
                  
                  // 휴게시간 차감
                  const breakTime = schedule.break_time;
                  let breakMinutes = 0;
                  if (breakTime === '30분') breakMinutes = 30;
                  else if (breakTime === '1시간') breakMinutes = 60;
                  else if (breakTime === '1시간 30분') breakMinutes = 90;
                  else if (breakTime === '2시간') breakMinutes = 120;
                  else if (breakTime === '2시간 30분') breakMinutes = 150;
                  else if (breakTime === '3시간') breakMinutes = 180;
                  else if (breakTime === '3시간 30분') breakMinutes = 210;
                  else if (breakTime === '4시간') breakMinutes = 240;
                  else if (breakTime === '4시간 30분') breakMinutes = 270;
                  else if (breakTime === '5시간') breakMinutes = 300;
                  
                  totalWorkHours -= breakMinutes / 60;
                  const totalWorkHoursStr = formatHoursAndMinutes(Math.max(0, totalWorkHours));
                  
                  return (
                    <Paper
                      key={schedule.id}
                      sx={{
                        p: 1.25,
                        background: 'var(--bg-elev-1)',
                        borderLeft: accentBorder,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: 'var(--bg-muted)',
                          transform: 'translateY(-1px)',
                          boxShadow: 'var(--shadow-1)',
                        },
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleScheduleDialogOpen(undefined, schedule);
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                            {employeeName}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: 'var(--fg-secondary)', mb: 0.5 }}>
                            템플릿: {templateName}
                          </Typography>

                          {time1Display && (
                            <Typography variant="body2" sx={{ color: 'var(--fg-default)' }}>
                              {time1Display}
                            </Typography>
                          )}
                          {time2Display && (
                            <Typography variant="body2" sx={{ color: 'var(--fg-default)' }}>
                              {time2Display}
                            </Typography>
                          )}

                          <Typography variant="body2" sx={{ color: 'var(--fg-default)', fontWeight: 600, mt: 0.5 }}>
                            총 근무시간: {totalWorkHoursStr}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'var(--fg-tertiary)' }}>
                            휴게시간: {breakTime || '없음'}
                          </Typography>
                        </Box>
                        <Button
                          variant="text"
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleScheduleDelete(schedule);
                          }}
                        >
                          삭제
                        </Button>
                      </Box>
                    </Paper>
                  );
                } else {
                  // 커스텀 스케줄
                  const startTime = parseTime(schedule.start_time);
                  const endTime = parseTime(schedule.end_time);
                  
                  let timeDisplay = '';
                  if (startTime && endTime && !isNaN(startTime.getTime()) && !isNaN(endTime.getTime())) {
                    timeDisplay = `${startTime.toLocaleTimeString('ko-KR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })} - ${endTime.toLocaleTimeString('ko-KR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}`;
                  }
                  
                  return (
                    <Paper
                      key={schedule.id}
                      sx={{
                        p: 1.25,
                        background: 'var(--bg-elev-1)',
                        borderLeft: accentBorder,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: 'var(--bg-muted)',
                          transform: 'translateY(-1px)',
                          boxShadow: 'var(--shadow-1)',
                        },
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleScheduleDialogOpen(undefined, schedule);
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                            {employeeName}
                          </Typography>
                          {timeDisplay ? (
                            <Typography variant="body2" sx={{ color: 'var(--fg-secondary)' }}>
                              {timeDisplay}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'var(--fg-tertiary)', fontStyle: 'italic' }}>
                              시간 정보 없음
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: 'var(--fg-tertiary)' }}>
                            휴게시간: {schedule.break_time || '없음'}
                          </Typography>
                        </Box>
                        <Button
                          variant="text"
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleScheduleDelete(schedule);
                          }}
                        >
                          삭제
                        </Button>
                      </Box>
                    </Paper>
                  );
                }
              })}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // 로딩 상태 표시
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '50vh',
        gap: 2
      }}>
        <Typography variant="h6" color="text.secondary">
          캘린더 데이터를 불러오는 중...
        </Typography>
      </Box>
    );
  }

  // 에러 상태 표시
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          캘린더 데이터를 불러오는데 실패했습니다: {error}
        </Alert>
        {renderCalendarHeader()}
      </Box>
    );
  }

  // 시간표(주간 시간 그리드) — 구글 캘린더식 드래그 편집
  const renderTimeline = () => {
    const weekStart = new Date(selectedDate);
    weekStart.setDate(selectedDate.getDate() - selectedDate.getDay());
    weekStart.setHours(0, 0, 0, 0);
    return (
      <WeekTimeGrid
        weekStart={weekStart}
        schedules={schedules}
        employees={employees}
        employeeColors={employeeColors}
        onCommit={handleGridCommit}
        onEdit={(schedule) => handleScheduleDialogOpen(undefined, schedule)}
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {renderCalendarHeader()}
      {copyMode && (
        <CopyModeBanner
          step={copySourceDate ? 'target' : 'source'}
          sourceLabel={sourceLabel}
          scheduleCount={sourceSchedules.length}
          targetCount={copyTargetDates.length}
          loading={copying}
          onExecute={handleCopyExecute}
          onCancel={exitCopyMode}
        />
      )}
      {viewMode === 'timeline' ? (
        renderTimeline()
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
            {renderSelectedDateDetails()}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {viewMode === 'month' ? renderMonthCalendar() : renderWeekCalendar()}
          </Box>
        </Box>
      )}
      
      {/* 플로팅 액션 버튼 (복사 모드에서는 숨김 — 하단 '복사 실행' 바와 겹침 방지) */}
      {!copyMode && (
        <Fab
          variant="extended"
          color="primary"
          onClick={() => handleScheduleDialogOpen()}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: 'var(--primary)',
            '&:hover': {
              background: 'var(--primary-hover)',
            },
          }}
        >
          일정 추가
        </Fab>
      )}
      
      {/* 근무 일정 폼 다이얼로그 */}
      <WorkScheduleForm
        open={scheduleDialogOpen}
        onClose={handleScheduleDialogClose}
        schedule={selectedSchedule}
        selectedDate={clickedDate || undefined}
      />

      {/* 드롭 이동 후속 팝업 — "시간도 수정할까요?" → 예: 간략 시간 편집 */}
      <Dialog open={moveFollowUp !== null} onClose={closeFollowUp} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingTime ? '시간 수정' : '일정을 옮겼습니다'}
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {moveFollowUp && !editingTime && (
            <Typography sx={{ fontSize: 16, color: 'var(--fg-secondary)', lineHeight: 1.6 }}>
              <strong>{moveFollowUp.name}</strong>님 일정을{' '}
              <strong>
                {moveFollowUp.targetDate.toLocaleDateString('ko-KR', {
                  month: 'long',
                  day: 'numeric',
                  weekday: 'short',
                })}
              </strong>
              로 옮겼습니다. 시간도 수정할까요?
            </Typography>
          )}
          {moveFollowUp && editingTime && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                  size="small"
                  type="time"
                  label="출근 시간"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 600 }}
                  value={timeForm.start}
                  onChange={(e) => setTimeForm((f) => ({ ...f, start: e.target.value }))}
                />
                <TextField
                  size="small"
                  type="time"
                  label="퇴근 시간"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 600 }}
                  value={timeForm.end}
                  onChange={(e) => setTimeForm((f) => ({ ...f, end: e.target.value }))}
                />
              </Box>
              <Typography sx={{ fontSize: 13, color: 'var(--fg-tertiary)', lineHeight: 1.5 }}>
                {moveFollowUp.wt2
                  ? '2구간 근무 — 출근은 1차 시작, 퇴근은 2차 종료 시각으로 적용됩니다.'
                  : '퇴근이 출근보다 이르면 다음 날(야간 근무)로 저장됩니다.'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!editingTime ? (
            <>
              <Button onClick={closeFollowUp}>아니요</Button>
              <Button
                variant="contained"
                disableElevation
                onClick={openQuickTimeEdit}
                sx={{
                  backgroundColor: 'var(--primary)',
                  '&:hover': { backgroundColor: 'var(--primary-hover)' },
                }}
              >
                예, 시간 수정
              </Button>
            </>
          ) : (
            <>
              <Button onClick={closeFollowUp} disabled={savingTime}>
                취소
              </Button>
              <Button
                variant="contained"
                disableElevation
                disabled={savingTime || !timeForm.start || !timeForm.end}
                onClick={handleQuickTimeSave}
                sx={{
                  backgroundColor: 'var(--primary)',
                  '&:hover': { backgroundColor: 'var(--primary-hover)' },
                }}
              >
                {savingTime ? '저장 중...' : '저장'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* 드래그 이동 결과 알림 */}
      <Snackbar
        open={moveSnackbar !== null}
        autoHideDuration={3000}
        onClose={() => setMoveSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setMoveSnackbar(null)}>
          {moveSnackbar}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!copyResultSnackbar}
        autoHideDuration={4000}
        onClose={() => setCopyResultSnackbar(null)}
        message={copyResultSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default CalendarTab;
